import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getHasAllCustomersAccess, getUserAllowedCustomers } from '../../../../../_augmentt/selectors/userPermissions'
import { useGetCustomerCollectionsQuery } from '../../../../../_augmentt/services/customerCollections'
import { getOrgPermissionLevels, getPermissionEntities } from '../../../../crud/orgs.crud'
import { getSystemUsers } from '../../../../crud/users.crud'
import { getSystemUserRole, getSystemUserRoleMessageId } from '../../../../util/userRoles.util'
import { getAssignableCustomers } from '../SystemUserFields'

const EMPTY_LIST = []
const EMPTY_DETAILS = {
  systemUser: null,
  permissionLevels: EMPTY_LIST,
  permissionEntities: EMPTY_LIST,
  allCustomers: EMPTY_LIST,
}
// The System User tab shows the linked account's status, which only the latest snapshot holds.
const SYSTEM_USERS_PARAMS = { includeM365Properties: true }

/**
 * Everything the System User tab and its edit panel need for one system user. System Users already holds
 * it all and passes `providedDetails`, so the tab shows at once; elsewhere it loads here. Nothing loads
 * or shows while `enabled` is false.
 */
const useSystemUserDetails = ({ systemUserId, providedDetails = null, enabled }) => {
  const intl = useIntl()
  const organizationId = useSelector(({ user }) => user.data?.organization)
  const hasAllCustomersAccess = useSelector(getHasAllCustomersAccess)
  const allowedCustomers = useSelector(getUserAllowedCustomers)
  const isActive = enabled && !!systemUserId && !!organizationId
  const shouldLoad = isActive && !providedDetails

  const [details, setDetails] = useState(EMPTY_DETAILS)
  const [isLoading, setLoading] = useState(false)
  const [reloadCount, setReloadCount] = useState(0)

  const { data: customerCollections = EMPTY_LIST, isFetching: isFetchingCustomerCollections } =
    useGetCustomerCollectionsQuery(organizationId, { skip: !isActive })

  useEffect(() => {
    if (!shouldLoad) return undefined
    let isCurrent = true

    const load = async () => {
      setLoading(true)
      try {
        const [users, permissionLevels, permissionEntities, allCustomers] = await Promise.all([
          getSystemUsers(organizationId, SYSTEM_USERS_PARAMS),
          getOrgPermissionLevels(),
          getPermissionEntities(),
          getAssignableCustomers({ hasAllCustomersAccess, allowedCustomers }),
        ])
        if (!isCurrent) return
        setDetails({
          // The list only holds users the viewer may see, so a missing user gets no tab.
          systemUser: users.data.find((user) => user.id === systemUserId) ?? null,
          permissionLevels: permissionLevels.data,
          permissionEntities: permissionEntities.data,
          allCustomers,
        })
      } catch (error) {
        console.error('Error loading the system user', error)
        if (isCurrent) setDetails(EMPTY_DETAILS)
      } finally {
        if (isCurrent) setLoading(false)
      }
    }
    load()

    return () => {
      isCurrent = false
    }
  }, [shouldLoad, organizationId, systemUserId, hasAllCustomersAccess, allowedCustomers, reloadCount])

  // Provided details are refreshed by their owner, so this only reloads details loaded here.
  const refresh = useCallback(() => setReloadCount((count) => count + 1), [])

  let currentDetails = EMPTY_DETAILS
  if (isActive) currentDetails = providedDetails ?? details

  const roleLabel = useMemo(() => {
    if (!currentDetails.systemUser) return ''
    const role = getSystemUserRole(currentDetails.systemUser, currentDetails.permissionEntities)
    return intl.formatMessage({ id: getSystemUserRoleMessageId(role) })
  }, [currentDetails.systemUser, currentDetails.permissionEntities, intl])

  return {
    ...currentDetails,
    customerCollections,
    isFetchingCustomerCollections,
    roleLabel,
    isLoading: shouldLoad && isLoading,
    refresh,
  }
}

export default useSystemUserDetails
